import { useState } from "preact/hooks";
import { api } from "../api/client.ts";
import type { ExportEstimate, ExportLink, SpaceOut } from "../api/types.gen.ts";
import { type Key, locale, t } from "../i18n/index.ts";
import { formatBytes, formatNumber } from "../lib/format.ts";
import { heldUrl, spaceApi, spaceUrl } from "../lib/paths.ts";
import { useAction, useResource } from "../lib/resource.ts";
import { atLeast } from "../lib/roles.ts";
import { type PageProps, reloadAndGoHome } from "../lib/state.ts";
import { toast } from "../lib/toast.ts";
import { zoneOptions } from "../lib/zone.ts";
import { Button, Link } from "../ui/Button.tsx";
import { ConfirmButton, Dialog, DialogActions } from "../ui/Dialog.tsx";
import { Disclosure } from "../ui/Disclosure.tsx";
import { Checkbox, Field, Form, Input, Radios, Select, Switch } from "../ui/form.tsx";
import { Async, CopyButton, Section } from "../ui/misc.tsx";
import { buttonClass, LINK } from "../ui/styles.ts";
import { SettingsNav } from "./settings/nav.tsx";
import { useSaveSettings } from "./settings/save.ts";

function General({ space, admin }: { space: SpaceOut; admin: boolean }) {
  const [name, setName] = useState(space.name);
  const [zone, setZone] = useState(space.time_zone);
  const [patch, busy] = useSaveSettings(spaceApi(space));
  return (
    <Section title={t("settings.general")}>
      <Form class="flex max-w-xl flex-col gap-4" onSubmit={() => patch({ name, time_zone: zone })}>
        <Field label={t("settings.name")}>
          <Input value={name} onValue={setName} required={true} disabled={!admin} />
        </Field>
        <Field label={t("settings.timeZone")} hint={t("settings.timeZoneHint")} anchor="time-zone">
          <Select value={zone} onValue={setZone} disabled={!admin} options={zoneOptions(zone)} />
        </Field>
        {admin ? (
          <Button class="self-start" submit={true} busy={busy}>
            {t("common.save")}
          </Button>
        ) : null}
      </Form>
    </Section>
  );
}

function Email({ space, admin }: { space: SpaceOut; admin: boolean }) {
  const [patch] = useSaveSettings(spaceApi(space));
  return (
    <Section title={t("settings.triageEmailAddress")} anchor="triage-email-address">
      <p class="mb-3 flex flex-wrap items-center gap-3 text-sm">
        <code class="rounded bg-zinc-100 px-2 py-1">{space.triage_email_address}</code>
        <CopyButton text={space.triage_email_address} label={t("common.copy")} />
        {admin ? (
          <ConfirmButton
            size="sm"
            label={t("settings.rotate")}
            title={t("settings.rotate")}
            message={t("settings.rotateMessage")}
            confirmLabel={t("settings.rotate")}
            onConfirm={() => patch({ rotate_triage_email_address: true })}
          />
        ) : null}
      </p>
      <p class="text-sm text-zinc-600">
        {t("settings.triageEmailAddressAccepts")}{" "}
        <Link href={spaceUrl(space, "/settings/org")} class={LINK}>
          {t("settings.allowlistLink")}
        </Link>
      </p>
      <p class="mt-1 text-sm text-zinc-600">
        <Link href={heldUrl(space)} class={LINK}>
          {t("settings.triageEmailAddressHeld")}
        </Link>
      </p>
      <div class="mt-3">
        <ForwardingGuide />
      </div>
    </Section>
  );
}

/** How to forward mail here from Gmail and Outlook, folded until asked for. */
function ForwardingGuide() {
  const steps = (keys: readonly Key[]) => (
    <ol class="ml-5 list-decimal space-y-1">
      {keys.map((k) => (
        <li key={k}>{t(k)}</li>
      ))}
    </ol>
  );
  return (
    <Disclosure summary={t("settings.forward.title")}>
      <div class="mt-2 flex max-w-2xl flex-col gap-4 text-sm text-zinc-700">
        <div>
          <h3 class="mb-1 font-medium text-zinc-900">{t("held.provider.gmail")}</h3>
          {steps(["settings.forward.gmail1", "settings.forward.gmail2", "settings.forward.gmail3"])}
        </div>
        <div>
          <h3 class="mb-1 font-medium text-zinc-900">{t("settings.forward.outlook")}</h3>
          {steps(["settings.forward.outlook1", "settings.forward.outlook2"])}
        </div>
        <p class="text-zinc-600">{t("settings.forward.note")}</p>
      </div>
    </Disclosure>
  );
}

function Locks({ space, admin }: { space: SpaceOut; admin: boolean }) {
  const [patch, busy] = useSaveSettings(spaceApi(space));
  return (
    <Section title={t("settings.locks")} anchor="locks">
      <div class="flex flex-col gap-2">
        <Switch
          label={t("settings.schemaLocked")}
          checked={space.schema_locked}
          disabled={!admin || busy}
          onCheck={(v) => patch({ schema_locked: v })}
        />
        <Switch
          label={t("settings.rulesLocked")}
          checked={space.rules_locked}
          disabled={!admin || busy}
          onCheck={(v) => patch({ rules_locked: v })}
        />
      </div>
    </Section>
  );
}

/**
 * The export's choices: with or without files (their count and size beside), the archive's size at most. The
 * download is the browser's own (a short-lived link), so it shows progress and doesn't fill the page's memory.
 */
function ExportDialog({ space, open, onClose }: { space: SpaceOut; open: boolean; onClose: () => void }) {
  return (
    <Dialog open={open} onClose={onClose} title={t("settings.exportTitle")}>
      <ExportChoices space={space} onClose={onClose} />
    </Dialog>
  );
}

function ExportChoices({ space, onClose }: { space: SpaceOut; onClose: () => void }) {
  const [files, setFiles] = useState(true);
  const [history, setHistory] = useState<"full" | "current">("full");
  const estimate = useResource(`export-estimate:${space.id}`, (signal) =>
    api.get<ExportEstimate>(spaceApi(space, "/export/estimate"), undefined, signal),
  );
  const [start, starting] = useAction(async () => {
    const link = await api.post<ExportLink>(spaceApi(space, "/export/link"), {
      files,
      history: history === "full",
      locale: locale.value,
    });
    const a = document.createElement("a");
    a.href = link.url; // served as an attachment: the page stays
    a.click();
    toast(t("settings.exportStarted"));
    onClose();
  });
  const e = estimate.data;
  return (
    <div class="flex flex-col gap-4">
      <p class="text-sm text-zinc-600">{t("settings.exportIntro")}</p>
      <Async r={estimate}>
        {(est) => (
          <>
            <Checkbox
              label={t("settings.exportFiles", {
                count: est.files,
                n: formatNumber(est.files),
                size: formatBytes(est.files_bytes),
              })}
              checked={files}
              disabled={est.files === 0}
              onCheck={setFiles}
            />
            <Radios
              label={t("settings.exportHistory")}
              value={history}
              onValue={setHistory}
              options={[
                {
                  value: "full",
                  label: t("settings.exportHistory.full"),
                  hint: t("settings.exportHistory.fullHint"),
                },
                {
                  value: "current",
                  label: t("settings.exportHistory.current"),
                  hint: t("settings.exportHistory.currentHint"),
                },
              ]}
            />
            <p class="text-sm text-zinc-500">
              {t("settings.exportSize", {
                size: formatBytes(
                  est.data_bytes + (files ? est.files_bytes : 0) + (history === "full" ? est.history_bytes : 0),
                ),
              })}
            </p>
          </>
        )}
      </Async>
      <DialogActions>
        <Button answer="cancel" onClick={onClose}>
          {t("common.cancel")}
        </Button>
        <Button answer="confirm" variant="primary" busy={starting} disabled={!e} onClick={() => start()}>
          {t("settings.exportDownload")}
        </Button>
      </DialogActions>
    </div>
  );
}

/** Export (admins) and deletion (owners, typing the space's name to confirm; an org keeps at least one space). */
function Data({ space }: { space: SpaceOut }) {
  const [exporting, setExporting] = useState(false);
  const [remove, removing] = useAction(async () => {
    await api.del(spaceApi(space));
    await reloadAndGoHome();
  });
  return (
    <Section title={t("settings.data")} anchor="data">
      <div class="flex flex-wrap gap-3">
        <Button onClick={() => setExporting(true)}>{t("settings.export")}</Button>
        <Link href={spaceUrl(space, "/settings/import")} class={buttonClass()}>
          {t("import.start")}
        </Link>
        <ExportDialog space={space} open={exporting} onClose={() => setExporting(false)} />
        {space.role === "owner" ? (
          <ConfirmButton
            variant="danger"
            label={t("settings.deleteSpace")}
            title={t("settings.deleteSpace")}
            message={t("settings.deleteSpaceMessage")}
            confirmLabel={t("settings.deleteSpace")}
            typeToConfirm={space.name}
            busy={removing}
            onConfirm={remove}
          />
        ) : null}
      </div>
    </Section>
  );
}

/** The space's own settings: name, zone, triage email address, locks, export and deletion. */
export function SpaceSettings({ space }: PageProps) {
  const admin = atLeast(space.role, "admin");
  return (
    <>
      <SettingsNav space={space} tab="" />
      <General space={space} admin={admin} />
      <Email space={space} admin={admin} />
      <Locks space={space} admin={admin} />
      {admin ? <Data space={space} /> : null}
    </>
  );
}
